import { Link } from "react-router-dom";
import { Pill, ShieldCheck, BarChart3, Bug, Mail } from "lucide-react";

/**
 * Online Tracking Disclosure required by the HHS 2024 Online Tracking Bulletin
 * for HIPAA-regulated healthcare websites. Plain-language inventory of every
 * 3rd-party tracker, what it receives, and how to opt out.
 *
 * Linked from the cookie banner and the footer.
 */
export default function OnlineTrackingDisclosurePage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="online-tracking-page">
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-300" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Online Tracking Technologies
          </h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          Last updated: February 2026 · Disclosure required by HHS OCR 2024 Online Tracking Technology Bulletin
        </p>

        <div className="bg-white rounded-2xl border border-purple-100 p-6 sm:p-8 space-y-6">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Plain-language summary</h2>
            <p className="text-slate-700 leading-relaxed">
              We use a small number of third-party tools to keep the site working and to help us measure
              <b> anonymous usage patterns</b>. We <b>never</b> share your name, email, date of birth, medical
              history, weight, prescriptions, or any other Protected Health Information (PHI) with any tracker,
              advertising network, or social-media platform. We do <b>not</b> run advertising pixels (no Meta
              Pixel, no Google Ads, no TikTok Pixel, no LinkedIn Insight Tag).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">Trackers we use</h2>

            <div className="space-y-4">
              <TrackerCard
                icon={<ShieldCheck className="w-4 h-4" />}
                color="emerald"
                name="Necessary (always on)"
                purpose="Login session cookies + CSRF token. Required for the site to work — there's no opt-out, but they only live in your browser and contain no PHI."
                receives="Authentication token (random string), tab session id (random string). Nothing identifying."
                category="Strictly necessary"
                optout="Not applicable — cookies are deleted when you log out or clear your browser."
              />

              <TrackerCard
                icon={<BarChart3 className="w-4 h-4" />}
                color="purple"
                name="PostHog (product analytics)"
                purpose="Anonymous page-view counts and feature-usage metrics that help us improve the product."
                receives="A one-way SHA-256 hash of your account id (we cannot reverse it), your role (patient / admin / prescriber / etc), the page path with all URL parameters stripped, and a coarse-grained event name (e.g. 'checkout_started'). Configured with: autocapture OFF, session recording OFF, IP address OFF, masked inputs ON. We have manually blocklisted $ip and $referrer."
                category="Analytics — REQUIRES YOUR CONSENT"
                optout="Decline 'analytics' in the cookie banner, or open it again from the footer and toggle off."
              />

              <TrackerCard
                icon={<Bug className="w-4 h-4" />}
                color="rose"
                name="Sentry / GlitchTip (error monitoring)"
                purpose="When the site crashes, we record the JavaScript stack trace so we can fix the bug."
                receives="The error message, the file/line where it happened, the page URL (with all query parameters stripped), and your browser version. We run a PHI scrubber that redacts anything that looks like an email, phone, address, dose number, or weight before the error is sent."
                category="Necessary for service reliability"
                optout="Sentry only fires on actual errors and never receives PHI by design. If you want it fully off, decline analytics in the cookie banner — Sentry's PII features are also gated by that toggle."
              />
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">What we do NOT do</h2>
            <ul className="list-disc pl-6 space-y-1.5 text-slate-700">
              <li>We do not run Meta Pixel, Google Ads tag, TikTok Pixel, LinkedIn Insight, X/Twitter Pixel, or any advertising tracker.</li>
              <li>We do not record your session video, capture form input values, or send mouse-movement data to any third party.</li>
              <li>We do not share PHI with chat widgets, marketing tools, A/B test tools, or heat-map providers.</li>
              <li>We do not sell, rent, or trade your data with any third party — full stop.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Change your mind</h2>
            <p className="text-slate-700 leading-relaxed">
              You can re-open the consent banner any time by clearing your browser&apos;s <code className="px-1 rounded bg-slate-100 font-mono text-xs">localStorage</code> for this site, or by emailing us at the address below.
            </p>
          </section>

          <section className="rounded-xl bg-slate-50 dark:bg-slate-800/40 p-5 border border-slate-200">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-purple-600 dark:text-purple-300 mt-0.5" />
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">Request data removal</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                  To delete or export your tracking-tool footprint, email
                  {" "}
                  <a href="mailto:customer.service@glpflo.com" className="text-purple-600 hover:underline font-semibold">customer.service@glpflo.com</a>
                  {" "}
                  with the subject line <b>&ldquo;Tracking data removal&rdquo;</b>. We respond within 30 days
                  (HIPAA §164.524 / §164.526).
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function TrackerCard({ icon, color, name, purpose, receives, category, optout }) {
  const colorClasses = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-700 dark:text-emerald-300",
    purple: "border-purple-200 bg-purple-50 text-purple-700 dark:bg-purple-950/30 dark:border-purple-700 dark:text-purple-300",
    rose: "border-rose-200 bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:border-rose-700 dark:text-rose-300",
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${colorClasses[color]}`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <h3 className="font-bold text-slate-900">{name}</h3>
            <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${colorClasses[color]}`}>{category}</span>
          </div>
          <p className="text-sm text-slate-600 mt-1.5"><b className="text-slate-700">Why we use it:</b> {purpose}</p>
          <p className="text-sm text-slate-600 mt-1.5"><b className="text-slate-700">What it receives:</b> {receives}</p>
          <p className="text-sm text-slate-600 mt-1.5"><b className="text-slate-700">How to opt out:</b> {optout}</p>
        </div>
      </div>
    </div>
  );
}
